/**
 * Entry Splash
 * Landing gate: first-time visitors see the animated logo splash and click
 * anywhere to enter the site. Gated by sessionStorage so it only shows once
 * per browser session, not on every internal navigation. This app is
 * single-tenant (Noosa only), so no slug check is needed — every visitor
 * sees it.
 */

import React, { useEffect, useRef, useState } from 'react';
import { AccessCodePrompt } from 'shared-ui';
import { usePortal } from '../context/PortalContext';
import { getApiBaseUrl } from '../services/api';
import './EntrySplash.css';

const SEEN_KEY = 'noosa_entry_splash_seen';
const PARTICLE_COLORS = ['#00A7D3', '#51C3C8', '#F69A78', '#FCE3A2', '#A37DB0'];
const PARTICLE_COUNT = 40;

const shouldShowSplash = () => {
  try {
    return !window.sessionStorage.getItem(SEEN_KEY);
  } catch (e) {
    // sessionStorage blocked (privacy mode, etc.) - skip the gate rather than break the site
    return false;
  }
};

const EntrySplash = () => {
  const { businessId } = usePortal();
  const [visible, setVisible] = useState(shouldShowSplash);
  const [leaving, setLeaving] = useState(false);
  const stageRef = useRef(null);
  const cardRef = useRef(null);
  const particleFieldRef = useRef(null);

  useEffect(() => {
    if (!visible || !particleFieldRef.current) return;
    const field = particleFieldRef.current;
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const p = document.createElement('div');
      p.className = 'entry-splash-particle';
      const size = 2 + Math.random() * 4;
      const color = PARTICLE_COLORS[Math.floor(Math.random() * PARTICLE_COLORS.length)];
      p.style.width = `${size}px`;
      p.style.height = `${size}px`;
      p.style.left = `${Math.random() * 100}vw`;
      p.style.bottom = '-5vh';
      p.style.background = color;
      p.style.boxShadow = `0 0 ${size * 2}px ${color}`;
      p.style.animationDuration = `${8 + Math.random() * 10}s`;
      p.style.animationDelay = `${Math.random() * 10}s`;
      field.appendChild(p);
    }
    return () => {
      field.innerHTML = '';
    };
  }, [visible]);

  if (!visible) return null;

  const handleMouseMove = (e) => {
    if (!stageRef.current || !cardRef.current) return;
    const r = stageRef.current.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    cardRef.current.style.transform = `rotateY(${x * 16}deg) rotateX(${-y * 16}deg)`;
  };

  const handleMouseLeave = () => {
    if (!cardRef.current) return;
    cardRef.current.style.transform = 'rotateY(0deg) rotateX(0deg)';
  };

  const enterSite = () => {
    if (leaving) return;
    setLeaving(true);
    try {
      window.sessionStorage.setItem(SEEN_KEY, '1');
    } catch (e) {
      // ignore - nothing to persist to if storage is blocked
    }
    setTimeout(() => setVisible(false), 400);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      enterSite();
    }
  };

  /**
   * A valid access code was entered. The splash must mark itself seen BEFORE navigating: it shows
   * whenever SEEN_KEY is unset, so without this it simply re-renders on top of /presentation and
   * the code looks like it did nothing (2026-09-23).
   */
  const unlockAndGoToPresentation = () => {
    try {
      window.sessionStorage.setItem(SEEN_KEY, '1');
    } catch (e) {
      // Storage blocked - still navigate; worst case the splash shows once more.
    }
    window.location.href = '/presentation';
  };

  return (
    <div
      className={`entry-splash${leaving ? ' entry-splash-leaving' : ''}`}
      role="button"
      tabIndex={0}
      aria-label="Enter T-Shirt Printing Noosa"
      onClick={enterSite}
      onKeyDown={handleKeyDown}
    >
      <div
        className="entry-splash-stage"
        ref={stageRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <div className="entry-splash-aurora">
          <div className="entry-splash-blob entry-splash-b1" />
          <div className="entry-splash-blob entry-splash-b2" />
          <div className="entry-splash-blob entry-splash-b3" />
          <div className="entry-splash-blob entry-splash-b4" />
        </div>
        <div className="entry-splash-vignette" />
        <div className="entry-splash-particles" ref={particleFieldRef} />

        <div className="entry-splash-card" ref={cardRef}>
          <div className="entry-splash-logo-wrap">
            <img src="/noosa/logo.svg" alt="T-Shirt Printing Noosa logo" />
            <div className="entry-splash-shine" />
          </div>
          <div className="entry-splash-tagline">T-Shirt Printing · Noosa</div>
          <div className="entry-splash-hint">Click anywhere to enter the site</div>
          {businessId && (
            <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
              <AccessCodePrompt
                apiBaseUrl={getApiBaseUrl()}
                businessId={businessId}
                onUnlocked={unlockAndGoToPresentation}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EntrySplash;
